<template>
	<div class="p-3 mb-2">
		<div class="font-bold text-gray-200 text-center bg-gray-750 border border-gray-700 rounded-md flex items-center justify-between">
			<button class="p-2" @click="prevMonth()" v-if="!cantChangeMonth">
				<ChevronLeftIcon class="h-6 w-6 text-gray-400" />
			</button>
			<span class="p-2 mx-auto">{{ dateUtils.getLongMonth(dateSelected.month) }} {{ dateSelected.year }}</span>
			<button class="p-2" @click="nextMonth()" v-if="!cantChangeMonth">
				<ChevronRightIcon class="h-6 w-6 text-gray-400" />
			</button>
		</div>

		<div class="grid grid-cols-7 gap-y-2 font-bold text-sm text-center">
			<div class="py-2 rounded-md text-gray-400" v-for="day in days">{{ day }}</div>
			<div class="py-2 rounded-md" v-for="i in firstDayOfMonth"></div>
			<button
				:disabled="!hasDrops(day.date, day.month, day.year)"
				@click="dateActionCaller(day.date, day.month, day.year)"
				class="py-2 rounded-md relative transition-colors" v-for="day in month"
				:class="{
					'text-white bg-gray-700 hover:bg-gray-600': isSelectedDay(day.date) && hasDrops(day.date, day.month, day.year),
					'text-gray-300': !isSelectedDay(day.date),
					'hover:text-white hover:bg-gray-750': hasDrops(day.date, day.month, day.year)
				}"
			>
				<span>{{ day.date }}</span>
				<span
					v-if="hasDrops(day.date, day.month, day.year)"
					class="w-1.5 h-1.5 absolute left-1/2 transform -translate-x-1/2 translate-y-1/2 bottom-0 rounded-full"
					:class="isToday(day.date) ? 'bg-green-500' : 'bg-gray-600'"
				/>
			</button>
		</div>
	</div>
</template>

<script lang="ts" setup>

import { reactive, ref } from 'vue'
import dateUtils from '../util/dateUtils'
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/vue/24/solid'
import {DropCalendarDate} from '../types/Calendar'
import {Drop as DropEntity} from  "../api";

const emits = defineEmits(['loadMonth'])

const props = defineProps<{
	dateAction: Function,
	cantChangeMonth?: boolean,
	dates: {
		[date: string]: DropEntity[]
	}
}>()

const hasDrops = (date: number, month: number, year: number) => {
	if (!props.dates[`${year}-${month}-${date}`]) return 0
	else return props.dates[`${year}-${month}-${date}`].length
}

const today = new Date()
const dateSelected = reactive({
	year: today.getFullYear(),
	month: today.getMonth(),
	day: today.getDate()
})

const isSelectedDay = (day: number) => day === dateSelected.day
const isToday = (day: number) => day === today.getDate() && dateSelected.year === today.getFullYear() && dateSelected.month === today.getMonth()

const days = dateUtils.getDays()
const firstDayOfMonth = ref<number>(dateUtils.getFirstDayInMonth(dateSelected.year, dateSelected.month))
const month = ref<DropCalendarDate[]>([])

const setMonth = () => {
	emits('loadMonth', dateSelected.year, dateSelected.month + 1)
	month.value = []
	firstDayOfMonth.value = dateUtils.getFirstDayInMonth(dateSelected.year, dateSelected.month)
	for (let i = 0; i < dateUtils.getDaysInMonth(dateSelected.year, dateSelected.month); i++) {
		let date: DropCalendarDate = {
			year: dateSelected.year,
			month: dateSelected.month,
			date: i+1,
		}
		month.value.push(date)
	}
}

setMonth()

const prevMonth = () => {
	if (dateSelected.month === 0) {
		dateSelected.month = 11
		dateSelected.year--
	} else {
		dateSelected.month--
	}

	dateSelected.day = 1
	setMonth()
}

const nextMonth = () => {
	if (dateSelected.month === 11) {
		dateSelected.month = 0
		dateSelected.year++
	} else {
		dateSelected.month++
	}

	dateSelected.day = 1

	setMonth()
}

const dateActionCaller = (day: number, month: number, year: number) => {
	dateSelected.day = day
	props.dateAction(day, month, year)
}

</script>
